import React, { useEffect, useMemo, useRef, useState } from "react";
import Pulse from "../Loader/Pulse";
import {
  ArrowLeft,
  ArrowRight2,
  Cross,
  OrderBook,
} from "../CustomComponents/SwiftIcon/Icons";
import { COLOR_VARS } from "../../exports/ColorVars";
import ServerRequest from "../../exports/ServerRequest";
import { GetCookie } from "../../exports/CookieManagement";
import SwiftModal from "../CustomComponents/SwiftModal/SwiftModal";
import {
  isEmpty,
  numberFormat,
  priceFormat,
  formatSchemeName,
} from "../../exports/UtilFunctions";
import CustomIconButton from "../Body/CustomChartComponents/CustomIconButton/CustomIconButton";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import CustomSelect from "../Body/CustomChartComponents/CustomSelect/CustomSelect";
import MFChartContainer from "./MFChartContainer.js";
import MFPerformanceMetrics from "./MFPerformanceMetrics.jsx";
import MFBuySell from "./MFBuySell.jsx";
import MFSell from "./MFSellContainer.jsx";
import MFSip from "./MFSipContainer.jsx";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert.js";
import sipbutton from "../../assets/icons/mfsip.svg";
import sipbuttonblack from "../../assets/icons/sip-black.svg";
import sellbutton from "../../assets/icons/mfsell.svg";
import sellbuttonblack from "../../assets/icons/sell-black.svg";
import buybuttonblack from "../../assets/icons/buy-black.svg";
import buybutton from "../../assets/icons/mfbuy.svg";
import portfoliobuttonblack from "../../assets/icons/portfolio-black.svg";
import selectblack from "../../assets/icons/select-black.svg";
import PortfolioSearchBar from "../Body/MenuSection/PortfolioComponents/SearchBar.js";
import {
  PortfolioCashPosition,
  PortfolioChange,
  PortfolioInvestment,
  PortfolioProfitLoss,
  PortfolioValue,
} from "../StrategyManagement.js/PortfolioComponents.js";
import Card from "../ui/card.jsx";

const DEVICE_SIZE = getDeviceSize();

const warning_config = {
  Band: true,
  BandColor: "var(--main-blue)",
  BoxColor: "var(--white)",
  TextColor: "var(--text-color)",
  AutoClose: {
    Active: true,
    Line: true,
    LineColor: "var(--main-blue)",
    Time: 3,
  },
};

const  Portfolio = ({ customer }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("Fetching your portfolio");
  const [opacityMsg, setOpacityMsg] = useState(true);
  const [transaction, setTransaction] = useState([]);

  const [popup, setPopup] = useState(false);
  const [popupData, setPopupData] = useState({});
  const [chartPopup, setChartPopup] = useState(false);
  const [finalpopup, setFinalpopup] = useState(false);
  const [error, setError] = useState("null");
  const [popupLoading, setPopupLoading] = useState(false);
  const [activeCard, setActiveCard] = useState(null);

  const [benchmarkData, setBenchmarkData] = useState([]);
  const [selectedBenchmarkObj, setSelectedBenchmarkObj] = useState(null);
  const [selectedBenchmark, setSelectedBenchmark] = useState("Nifty_50");
  const [benchmarkPerformance, setBenchmarkPerformance] = useState({});
  const [navPerformance, setNavPerformance] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [chartLoadingMsg, setChartLoadingMsg] = useState("Loading chart data");
  const [chartOpacityMsg, setChartOpacityMsg] = useState(true);

  const customOptions = [
    { label: "Buy", value: "Buy", icon: buybuttonblack },
    { label: "SIP", value: "SIP", icon: sipbuttonblack },
    {
      label: "Sell",
      value: "Sell",
      disabled: transaction.length === 0,
      icon: sellbuttonblack,
    },
    {
      label: "Portfolio Performance",
      value: "Portfolio Performance",
      disabled: transaction.length === 0,
      icon: portfoliobuttonblack,
    },
  ];

  const isSmallDevice = DEVICE_SIZE === "S";
  const account_token = GetCookie("token");
  const account_code = customer?.account_code;

  const isDefined = (...flags) => flags.every((v) => v !== undefined);

  useEffect(() => {
    if (!isDefined(chartLoading, metricsLoading)) return;

    const allLoaded = !chartLoading && !metricsLoading;
    setDetailsLoading(!allLoaded);
  }, [chartLoading, metricsLoading]);

  useEffect(() => {
    if (!detailsLoading) return;

    const chartLoadingMessageInterval = setInterval(() => {
      setChartLoadingMsg((prev) =>
        prev === "Loading chart data"
          ? "It may take some time"
          : "Loading chart data",
      );
    }, 3000);

    const chartOpacityInterval = setInterval(() => {
      setChartOpacityMsg((prev) => !prev);
    }, 1500);

    return () => {
      clearInterval(chartLoadingMessageInterval);
      clearInterval(chartOpacityInterval);
    }; // cleanup
  }, [detailsLoading]);

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTransactionWithNAV(),
        getBenchmarkPerformanceMetric(),
        getNavPerformanceMetric(),
      ]);
      setLoading(false);
    };

    loadAllData();
    const loadingMessageInterval = setInterval(() => {
      setLoadingMsg((prev) =>
        prev === "Fetching your portfolio"
          ? "It may take some time"
          : "Fetching your portfolio",
      );
    }, 3000);

    const opacityInterval = setInterval(() => {
      setOpacityMsg((prev) => !prev);
    }, 1500);

    // Cleanup
    return () => {
      clearInterval(loadingMessageInterval);
      clearInterval(opacityInterval);
    };
  }, [account_code, account_token]);

  useEffect(() => {
    const fetchBenchmarkData = async () => {
      // Only return if both token and account_code are missing
      if (!account_token && !account_code) return;

      try {
        const url = customer
          ? `/mf/benchmark/get?account_code=${account_code}` // Superuser
          : `/mf/benchmark/get?token=${account_token}`; // Customer

        const res = await ServerRequest({ url, method: "get" });

        if (!res.error && res.data) {
          setBenchmarkData(res.data);
        } else {
          console.warn("Benchmark fetch failed", res.message);
        }
      } catch (error) {
        console.error("Error fetching benchmark data:", error);
      }
    };

    fetchBenchmarkData();
  }, [account_token, account_code, selectedBenchmark]);

  const fetchTransactionWithNAV = async () => {
    if (!account_token && !account_code) return;

    try {
      const url = customer
        ? `/mf/portfolio?account_code=${account_code}` // Superuser
        : `/mf/portfolio?token=${account_token}`; // Customer

      const response = await ServerRequest({ url, method: "get" });

      const portfolioData = response?.data || [];

      // Separate rows
      const rowsWithISIN = portfolioData.filter((item) => item.isin);
      const footerRow = portfolioData.find((item) => !item.isin);

      // Enrich with NAV change
      const enrichedRows = await Promise.all(
        rowsWithISIN.map(async (item) => {
          try {
            const navResp = await ServerRequest({
              url: `/mf/latestnav/${item.isin}`,
              method: "get",
            });
            return { ...item, navChange: navResp?.result?.navChange ?? null };
          } catch {
            return { ...item, navChange: null };
          }
        }),
      );

      // Append footer row if it exists
      const finalData = footerRow ? [...enrichedRows, footerRow] : enrichedRows;
      const transactionData = finalData.filter(
        (p) => p.investment_value !== "0.00",
      );

      setTransaction(transactionData);
    } catch (err) {
      console.error("Error fetching portfolio/NAV data:", err);
      setTransaction([]); // fallback
    }
  };

  const getNavPerformanceMetric = async () => {
    // Exit only if BOTH are missing
    if (!account_token && !account_code) return;

    try {
      // Decide URL based on customer or superuser
      const url = customer
        ? `/mf/portdata?account_code=${account_code}` // Superuser
        : `/mf/portdata?token=${account_token}`; // Customer

      const response = await ServerRequest({ url, method: "get" });

      if (!response.error && response.data?.returnStats) {
        setNavPerformance(response.data.returnStats);
      } else {
        console.warn(
          "NAV Performance fetch failed",
          response.message || "No returnStats in response",
        );
      }
    } catch (error) {
      console.error("Error fetching NAV Performance:", error);
    }
  };

  const getBenchmarkPerformanceMetric = async () => {
    if (!selectedBenchmark) return;
    try {
      const response = await ServerRequest({
        url: `/mf/benchmark/get/${selectedBenchmark}`,
        method: "get",
      });
      if (!response.error && response.data?.returnStats) {
        setBenchmarkPerformance(response.data.returnStats);
      } else {
        console.warn("BenchMark Performance fetch failed", response.message);
      }
    } catch (error) {
      console.error("Error fetching BenchMark Performance:", error);
    }
  };

  const handleChart = () => {
    setChartPopup(true);
  };

  function handleBuy({ data, isSingle }) {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      setPopupData({
        type: "Buy",
        isSingle,
        isNewFund: true,
      });
    } else if (Array.isArray(data)) {
      const updatedDataArray = data.map((item) => ({
        ...item,
        type: "Buy",
        isSingle,
      }));
      setPopupData(updatedDataArray);
    } else {
      // 👉 Single existing fund
      const newData = { ...data, type: "Buy", isSingle };
      setPopupData(newData);
    }

    setPopup(true);
  }

  function handleSell({ data, isSingle }) {
    if (Array.isArray(data)) {
      // Data is an array of objects
      const updatedDataArray = data.map((item) => ({
        ...item,
        type: "Sell",
        isSingle,
      }));
      setPopupData(updatedDataArray);
    } else {
      // Data is a single object
      const newData = { ...data, type: "Sell", isSingle };
      setPopupData(newData);
    }
    setPopup(true);
  }

  async function handleSip({ data, isSingle }) {
    const res2 = await ServerRequest({
      url: `/nse/get-mandate-data?account_code=${customer.account_code}`,
      method: "get",
    });

    const mandates = res2?.data?.report_data || []; // assume array
    const hasValidMandate = mandates.some(
      (m) => m?.enachStatus?.toUpperCase() === "SUCCESS" && m?.umrnNo !== "",
    );
    if (!hasValidMandate) {
      Alert({
        TitleText: "Warning",
        Message:
          "No Mandate found. Please raise a ticket to setup mandate first.",
        ...warning_config,
      });
      setPopupData(null);
      setPopup(false);
      return;
    } else {
      if (!data || (Array.isArray(data) && data.length === 0)) {
        setPopupData({
          type: "SIP",
          isSingle,
          isNewFund: true,
        });
      } else if (Array.isArray(data)) {
        const updatedDataArray = data.map((item) => ({
          ...item,
          type: "SIP",
          isSingle,
        }));
        setPopupData(updatedDataArray);
      } else {
        const newData = { ...data, type: "SIP", isSingle };
        setPopupData(newData);
      }

      setPopup(true);
    }
  }

  return (
    <div className="mf-wrapper">
      {loading && transaction.length === 0 ? (
        <div className="app__login__screen">
          <div className="app__login__process">
            <Pulse />
            {/* <p className={opacityMsg ? "fadeIn" : "fadeOut"}>{loadingMsg}</p> */}
          </div>
        </div>
      ) : (
        <>
          {!customer && transaction.length !== 0 && DEVICE_SIZE !== "S" && (
            <div className="portfolio__options strategy">
              <div className="portfolio__header">
                <div className="portfolio__info__box">
                  <PortfolioValue
                    portfolio_value={
                      transaction[transaction.length - 1]?.investment_value
                    }
                  />
                </div>
                <div className="portfolio__info__box">
                  <PortfolioInvestment
                    portfolio_investment={
                      transaction[transaction.length - 1]?.investment_cost
                    }
                  />
                </div>
                <div className="portfolio__info__box">
                  <PortfolioProfitLoss
                    portfolio_profitloss={
                      transaction[transaction.length - 1]?.profit_loss
                    }
                  />
                </div>
                {/* <div className="portfolio__info__box">
                                  <PortfolioCashPosition cash={cash_position} />
                                </div> */}
                {/* <div className="portfolio__info__box">
                                  <PortfolioChange
                                    change_amount={change_amount}
                                    change_percentage={change_percentage}
                                  />
                                </div> */}
              </div>
              {/* <div className="portfolio__search__bar">
                                <PortfolioSearchBar addTableRow={AddStockToPortfolio} />
                              </div> */}
            </div>
          )}
          <div
            className={`mf-container ${customer ? "short" : ""} mf-transaction-container mf-portfolio-container`}
          >
            {transaction.length === 0 ? (
              <>
                <div className="mf-empty">
                  <OrderBook size={40} color={COLOR_VARS["SWIFT_COLOR4"]} />
                  <div>
                    <p>No Transactions found</p>
                    <span>Looks like you have't made any Transactions</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {DEVICE_SIZE === "S" ? (
                  <>
                    {/* <p className="details-txt">Summary</p> */}
                    <PortfolioConfig
                      investment={
                        transaction[transaction.length - 1].investment_cost
                      }
                      current_value={
                        transaction[transaction.length - 1].investment_value
                      }
                      returns={transaction[transaction.length - 1].total_return}
                      xirr={transaction[transaction.length - 1].xirr}
                    />
                    <p className="details-txt">Details</p>
                    <div className="mf-watchlist-card-container">
                      {transaction.slice(0, -1).map((h, index) => {
                        return (
                          <PortfolioCard
                            key={index}
                            data={h}
                            handleBuy={handleBuy}
                            handleSell={handleSell}
                            handleSip={handleSip}
                            isActive={activeCard === index}
                            onActivate={() => setActiveCard(index)}
                            onDeactivate={() => setActiveCard(null)}
                            isCustomer={customer}
                          />
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mf-portfilo-container">
                      <table>
                        <thead>
                          <tr>
                            <td className="scheme-name">Scheme Name</td>
                            <td className="isin">ISIN</td>
                            <td className="">Folio No</td>
                            <td className="category">Category</td>
                            <td className="">Units</td>
                            <td className="inv-value">
                              Inv. Value (Curr. Nav)
                            </td>
                            <td className="profit-loss">Profit/ Loss</td>
                            <td className="">Weight</td>
                            <td className="">Tot. Ret. / XIRR</td>
                            <td>Change</td>
                            <td className="hover-td"></td>
                          </tr>
                        </thead>
                        <tbody className="">
                          {transaction.map((h, index) => {
                            const isTotalRow = h.scheme_name === "TOTAL";
                            return (
                              <tr
                                key={index}
                                className={
                                  isTotalRow ? "total-row" : "yes_hover"
                                }
                              >
                                <td
                                  className={
                                    isTotalRow
                                      ? "td-hidden"
                                      : "body-scheme-name"
                                  }
                                >
                                  {isTotalRow ? "" : formatSchemeName(h.scheme_name, 0, false)}
                                </td>
                                <td className={isTotalRow ? "td-hidden" : ""}>
                                  {isTotalRow ? "" : h.isin}
                                </td>
                                <td className={isTotalRow ? "td-hidden" : ""}>
                                  {isTotalRow ? "" : h.folio_no}
                                </td>
                                <td
                                  className={
                                    isTotalRow ? "td-hidden" : "body-category"
                                  }
                                >
                                  {isTotalRow ? "" : h.category}
                                </td>
                                <td>
                                  {isTotalRow ? "" : numberFormat(h.units)}
                                </td>
                                <td className={isTotalRow ? "total-col" : ""}>
                                  {priceFormat(h.investment_value)}{" "}
                                  {isTotalRow
                                    ? ""
                                    : `(${priceFormat(h.current_nav)})`}
                                  <br />
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      color: "var(--font-secondary)",
                                    }}
                                  >
                                    {priceFormat(h.investment_cost)}{" "}
                                    {isTotalRow
                                      ? ""
                                      : `(${priceFormat(h.avg_cost)})`}
                                  </span>
                                </td>
                                <td
                                  className={`${Number(h.profit_loss) >= 0 ? "positive" : "negative"} ${isTotalRow ? "total-col" : ""}`}
                                >
                                  {priceFormat(h.profit_loss)}
                                </td>
                                <td className={isTotalRow ? "total-col" : ""}>
                                  {numberFormat(h.weight)}%
                                </td>
                                <td
                                  className={`${h.total_return >= 0 ? "positive" : "negative"} ${isTotalRow ? "total-col" : ""}`}
                                >
                                  {numberFormat(h.total_return)}%<br />
                                  {numberFormat(h.xirr)}%
                                </td>
                                <td
                                  className={`${h.change >= 0 ? "positive" : "negative"}`}
                                >
                                  {isTotalRow
                                    ? ""
                                    : `${numberFormat(h.navChange) + "%" || "--"}`}
                                </td>

                                {!isTotalRow && customer && (
                                  <div className="hover-container">
                                    <div
                                      className="hover-btn hover-input-btn"
                                      onClick={() =>
                                        handleBuy({ data: h, isSingle: true })
                                      }
                                    >
                                      {/* <img src={buybutton} alt="sipbutton" />{" "} */}
                                      Buy
                                    </div>
                                    <div
                                      onClick={() => {
                                        handleSell({ data: h, isSingle: true });
                                      }}
                                      className="hover-btn hover-input-btn"
                                    >
                                      {/* <img src={sellbutton} alt="sipbutton" />{" "} */}
                                      Sell
                                    </div>
                                    <div
                                      className="hover-btn hover-input-btn"
                                      onClick={() => {
                                        handleSip({ data: h, isSingle: true });
                                      }}
                                    >
                                      {/* <img src={sipbutton} alt="sipbutton" />{" "} */}
                                      SIP
                                    </div>
                                  </div>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "end" }}>
            {customer ? (
              <div
                className="portfolio_custom_select"
                style={{ width: "200px" }}
              >
                <CustomSelect
                  heading={""}
                  options={customOptions}
                  width={150}
                  height={40}
                  placeholder="Select Action"
                  defaultIndex={-1}
                  onTypeChange={(value) => {
                    if (value === "Buy")
                      handleBuy({
                        data: transaction.slice(0, -1),
                        isSingle: false,
                      });
                    else if (value === "Portfolio Performance") handleChart();
                    else if (value === "Sell")
                      handleSell({
                        data: transaction.slice(0, -1),
                        isSingle: false,
                      });
                    else if (value === "SIP")
                      handleSip({
                        data: transaction.slice(0, -1),
                        isSingle: false,
                      });
                    else alert("Not in Options");
                  }}
                />
              </div>
            ) : (
              <div className="report-section-button">
                <button
                  className="swift-button-icon"
                  onClick={() => setChartPopup(true)}
                >
                  <p style={{color:"var(--SWIFT_COLOR8)"}}>Portfolio Performance</p>
                  {/* <ArrowRight2 color={COLOR_VARS["SWIFT_COLOR8"]} size={14} /> */}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {chartPopup && (
        <SwiftModal
          closeModal={() => {
            setChartPopup(false);
          }}
          top="2%"
        >
          <div className="swift-mf-details-popup swift-mf-popup mf-container ">
            {detailsLoading && (
              <div className="app__login__screen">
                <div className="app__login__process">
                  <Pulse />
                  {/* <p className={chartOpacityMsg ? "fadeIn" : "fadeOut"}>
                    {chartLoadingMsg}
                  </p> */}
                </div>
              </div>
            )}
            <>
              {!detailsLoading && (
                <div className="swift-mf-popup-header">
                  <div
                    className="swift-account-svg"
                    onClick={() => {
                      setChartPopup(false);
                    }}
                  >
                    {isSmallDevice ? (
                      <Cross size={30} color={COLOR_VARS["SWIFT_COLOR4"]} />
                    ) : (
                      ""
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <p className="swift-mf-popup-name">
                      Composite Portfolio Performance
                      <br />
                      <span style={{ fontSize: "12px", fontWeight: "500" }}>
                        Simulated NAV of your portfolio taking into account your
                        inflows and outflows.
                      </span>
                    </p>
                    {isSmallDevice ? (
                      ""
                    ) : (
                      <div
                        className="swift-account-svg"
                        onClick={() => {
                          setChartPopup(false);
                        }}
                      >
                        <Cross size={30} color={COLOR_VARS["SWIFT_COLOR4"]} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="swift-mf-details-popup-body swift-mf-details-popup-body-flex">
                <>
                  {!detailsLoading && (
                    <div className="swift-mf-details-body-head">
                      <div className="swift-mf-head-left"></div>

                      <CustomSelect
                        defaultIndex={Math.max(
                          0,
                          benchmarkData.findIndex(
                            (b) =>
                              b.benchmark_common_name ===
                              selectedBenchmarkObj?.benchmark_common_name,
                          ),
                        )}
                        options={benchmarkData.map(
                          (b) => b.benchmark_common_name,
                        )}
                        width={240}
                        height={40}
                        placeholder="Select Benchmark"
                        onTypeChange={(value) => {
                          const selected = benchmarkData.find(
                            (b) => b.benchmark_common_name === value,
                          );
                          setSelectedBenchmarkObj(selected);
                          setSelectedBenchmark(
                            selected?.benchmark_database_name,
                          );
                        }}
                      />
                    </div>
                  )}
                  <div className="swift-mf-chart-section full-chart-wrapper">
                    <div className="chart-section-inner">
                      {isSmallDevice == "S" ? (
                        <MFChartContainer
                          selectedData={customer ? account_code : account_token}
                          const
                          isSuperuser={!!account_code && customer}
                          chartHeight={200}
                          chartWidth={1040}
                          selectedBenchmark={selectedBenchmark}
                          onChangeLoading={setChartLoading}
                          showContent={!detailsLoading}
                          chartType={"portfolio"}
                        />
                      ) : (
                        <MFChartContainer
                          selectedData={customer ? account_code : account_token}
                          const
                          isSuperuser={account_code && customer}
                          chartHeight={300}
                          chartWidth={1040}
                          selectedBenchmark={selectedBenchmark}
                          onChangeLoading={setChartLoading}
                          showContent={!detailsLoading}
                          chartType={"portfolio"}
                        />
                      )}
                    </div>
                  </div>
                  <div className="swift-mf-chart-performancemetric">
                    <MFPerformanceMetrics
                      navPerformance={navPerformance}
                      benchmarkPerformance={benchmarkPerformance}
                      onChangeLoading={setMetricsLoading}
                      showContent={!detailsLoading}
                    />
                  </div>
                </>
              </div>
            </>
          </div>
        </SwiftModal>
      )}
      {popup && (
        <SwiftModal
          closeModal={() => {
            setPopup(false);
            setError("null");
            setPopupData({});
            setPopupLoading(false);
          }}
          top="2%"
        >
          <div className="swift-mf-buysell-popup swift-mf-popup">
            {popupLoading == true ? (
              <div className="swift-mf-popup-loader">
                <Pulse />
{/* <p>Order in progress</p> */}
              </div>
            ) : (
              <>
                {(Array.isArray(popupData)
                  ? popupData[0]?.type === "Buy"
                  : popupData?.type === "Buy") && (
                  <MFBuySell
                    popupData={popupData}
                    portfolioData={transaction}
                    setPopup={setPopup}
                    setPopupData={setPopupData}
                    customer={customer}
                  />
                )}
                {(Array.isArray(popupData)
                  ? popupData[0]?.type === "Sell"
                  : popupData?.type === "Sell") && (
                  <MFSell
                    popupData={popupData}
                    portfolioData={transaction}
                    setPopup={setPopup}
                    customer={customer}
                  />
                )}

                {(Array.isArray(popupData)
                  ? popupData[0]?.type === "SIP"
                  : popupData?.type === "SIP") && (
                  <MFSip
                    popupData={popupData}
                    portfolioData={transaction}
                    setPopup={setPopup}
                    setPopupData={setPopupData}
                    customer={customer}
                  />
                )}
              </>
            )}
          </div>
        </SwiftModal>
      )}
      {finalpopup && (
        <SwiftModal closeModal={() => setFinalpopup(false)} top="2%">
          <div className="swift-mf-popup swift-mf-final-popup">
            <div className="swift-mf-popup-body swift-mf-final-popup-body">
              <p>Order placed successfully!</p>
              <p>
                You will receive an email with details of bank account where you
                need to pay for the order via NEFT/ RTGS..
              </p>
              <p>You have 12 hours to complete the transaction.</p>
            </div>
            <div className="swift-mf-popup-submit-btn">
              <CustomIconButton
                title="Close"
                icon={null}
                onClick={() => setFinalpopup(false)}
              />
            </div>
          </div>
        </SwiftModal>
      )}
    </div>
  );
};

function PortfolioCard({
  data,
  handleBuy,
  handleSell,
  handleSip,
  isActive,
  onActivate,
  onDeactivate,
  isCustomer,
}) {
  const {
    folio_no,
    current_nav,
    navChange,
    category,
    scheme_name,
    investment_cost,
    avg_cost,
    investment_value,
    profit_loss,
    units,
    xirr,
    total_return,
  } = data;

  const cardRef = useRef();
  const [schemeName, setSchemeName] = useState(null);

  useEffect(() => {
    if (scheme_name) {
      setSchemeName(formatSchemeName(scheme_name));
    }
  }, [scheme_name]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        onDeactivate();
      }
    }

    if (isActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isActive, onDeactivate]);

  return (
    // <div className="mf-watchlist-card" onClick={onActivate} ref={cardRef}>
    //   <div className="mfwc-info">
    //     <div className="isin-price">
    //       <p className="isin-type">
    //         Folio:&nbsp;<span>{folio_no}</span>
    //       </p>
    //       <p className="price-change">
    //         {priceFormat(current_nav)}&nbsp;&nbsp;
    //         <span className={navChange > 0 ? "positive" : "negative"}>
    //           ({numberFormat(navChange)}%)
    //         </span>
    //         &nbsp;
    //       </p>
    //     </div>
    //     <p className="name">{scheme_name}</p>
    //     <p className="category">{category}</p>
    //   </div>

    //   <div className="mfwc-config">
    //     <div className="mfwc-config-row">
    //       <p>
    //         Inv. Cost (Avg. Cost)
    //         <span>
    //           {priceFormat(investment_cost)}&nbsp;({priceFormat(avg_cost)})
    //         </span>
    //       </p>
    //       <p>
    //         Inv. Value
    //         <span>{priceFormat(investment_value)}</span>
    //       </p>
    //     </div>
    //     <div className="mfwc-config-row">
    //       <p>
    //         Profit/Loss
    //         <span className={profit_loss > 0 ? "positive" : "negative"}>
    //           {priceFormat(profit_loss)}
    //         </span>
    //       </p>
    //       <p>
    //         Units
    //         <span>{numberFormat(units)}</span>
    //       </p>
    //     </div>
    //     <div className="mfwc-config-row">
    //       <p>
    //         Total Return
    //         <span className={total_return >= 0 ? "positive" : "negative"}>
    //           {numberFormat(total_return)}%
    //         </span>
    //       </p>
    //       <p>
    //         XIRR
    //         <span className={xirr >= 0 ? "positive" : "negative"}>
    //           {numberFormat(xirr)}%
    //         </span>
    //       </p>
    //     </div>
    //   </div>

    //   {isCustomer && (
    //     <div className="hover-wrapper">
    //       {isActive && (
    //         <div className="sp__hover__options">
    //           <div
    //             className="sp__hover__buttons"
    //             onClick={() => handleBuy({ data: data, isSingle: true })}
    //           >
    //             <img src={buybutton} alt="sipbutton" /> Buy
    //           </div>
    //           <div
    //             className="sp__hover__buttons"
    //             onClick={() => handleSell({ data: data, isSingle: true })}
    //           >
    //             <img src={sellbutton} alt="sipbutton" /> Sell
    //           </div>
    //           <div
    //             className="sp__hover__buttons"
    //             onClick={() => handleSip({ data: data, isSingle: true })}
    //           >
    //             <img src={sipbutton} alt="sipbutton" /> SIP
    //           </div>
    //         </div>
    //       )}
    //     </div>
    //   )}
    // </div>

    <div className="mf-watchlist-card" onClick={onActivate} ref={cardRef}>
      <div className="mfwc-config">
        <div className="mfwc-config-row">
          <p>{schemeName && <p className="name">{schemeName}</p>}</p>
          <p className="value">
            {priceFormat(investment_value)}
          </p>
        </div>
        <div className="mfwc-config-row">
          <p className="category">{category}</p>
          <p>Folio:&nbsp;{folio_no}</p>
        </div>
        <div className="mfwc-config-row">
          <p>
            <span>
              {numberFormat(units)} @ ({priceFormat(avg_cost)})
            </span>
          </p>
          <p>
            {priceFormat(current_nav)}&nbsp;&nbsp;
            <span className={navChange > 0 ? "positive" : "negative"}>
              ({numberFormat(navChange)}%)
            </span>
          </p>
        </div>
        <div className="mfwc-config-row">
          <p>{priceFormat(investment_cost)}&nbsp;</p>
          <p>
            <span className={profit_loss > 0 ? "positive" : "negative"}>
              {priceFormat(profit_loss)}&nbsp;<sub>PnL</sub>
            </span>
            &nbsp;&nbsp;
            <span className={total_return >= 0 ? "positive" : "negative"}>
              {numberFormat(total_return)}%&nbsp;<sub>TR</sub>
            </span>
            &nbsp;&nbsp;
            <span className={xirr >= 0 ? "positive" : "negative"}>
              {numberFormat(xirr)}%&nbsp;<sub>XIRR</sub>
            </span>
            &nbsp;&nbsp;
          </p>
        </div>
      </div>

      {isCustomer && (
        <div className="hover-wrapper">
          {isActive && (
            <div className="sp__hover__options">
              <div
                className="sp__hover__buttons"
                onClick={() => handleBuy({ data: data, isSingle: true })}
              >
                {/* <img src={buybutton} alt="sipbutton" /> */} Buy
              </div>
              <div
                className="sp__hover__buttons"
                onClick={() => handleSell({ data: data, isSingle: true })}
              >
                {/* <img src={sellbutton} alt="sipbutton" /> */} Sell
              </div>
              <div
                className="sp__hover__buttons"
                onClick={() => handleSip({ data: data, isSingle: true })}
              >
                {/* <img src={sipbutton} alt="sipbutton" /> */} SIP
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PortfolioConfig({ investment, current_value, returns, xirr }) {
  return (
    <div className="portfolio-config">
      <div className="portfolio-rows">
        <div className="portfolio-info-box">
          <span>Current Value</span>
          <p>{priceFormat(current_value)}</p>
        </div>
      </div>
      <div className="portfolio-rows">
        <div className="portfolio-info-box">
          <span>Investment Cost</span>
          <p>{priceFormat(investment)}</p>
        </div>
        <div className="portfolio-info-box">
          <span>Total Return / XIRR</span>
          <div style={{ display: "flex", gap: "5px", fontSize: "14px" }}>
            <p className={returns >= 0 ? "positive" : "negative"}>
              {numberFormat(returns)}%
            </p>
            <span style={{ color: "var(--SWIFT_COLOR8)" }}>/</span>
            <p className={xirr >= 0 ? "positive" : "negative"}>
              {numberFormat(xirr)}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Portfolio;
